import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import { Button } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import isPdf from '../../../Utilities/isPdf';
import moment from 'moment';

import { tableStyles } from '../CaseDashboard.style';
import EnhancedTable from '../../Shared/EnhancedTable/EnhancedTable';
import {
  getOrderFileUrl,
  viewPdfInNewTab,
  downloadFile
} from '../../../Services/FileService';

const headCells = [
  { id: 'fileName', label: 'Document Name', width: '20%' },
  { id: 'description', label: 'Description', width: '20%' },
  { id: 'fileType', label: 'Document Type', width: '12%' },
  { id: 'sharedBy', label: 'Shared By', width: '12%' },
  { id: 'sharedAt', label: 'Share Date', width: '12%', isDate: true },
  { id: 'lastReviewedAt', label: 'Last Reviewed', width: '12%', isDate: true },
  {
    id: 'expiresAt',
    label: 'Expiration Date',
    width: '12%',
    isDate: true,
  },
  { id: 'records', label: 'Records', width: '12%' },
];

const CaseDocuments = (props) => {
  const {
    classes,
    expertCaseDocumentsList = [],
    orderId,
    isLoading,
    isDownloadAllowed
  } = props;

  // action buttons
  function ViewButton(props) {
    const { classes, row, setRow, isDownloadAllowed } = props;
    const [isViewingFile, setIsViewingFile] = useState(false);
    const [btnDownloading, setBtnDownloading] = useState(false);
    const [completed, setCompleted] = useState(0);
    const [variant, setVariant] = useState('indeterminate');
    const [isPdfFile, setIsPdfFile] = useState(false);
    const [disableDownloadButton, setDisableDownloadButton] = useState(false);

    useEffect(() => {
      const pdfFile = row && isPdf(row['fileName']);
      const disableDownload = !isDownloadAllowed && pdfFile;
      setIsPdfFile(pdfFile);
      setDisableDownloadButton(disableDownload);

      console.debug('Case Document: inside of useEffect, fileName:', row? row['fileName']: '', 'isPdf:', pdfFile, 'isDownloadAllowed:', isDownloadAllowed, 'disableDownloadButton:', disableDownload, 'row:', row);

    },[row, isDownloadAllowed])

    const onDownloadButtonClick = async () => {
      if (disableDownloadButton) return;

      try {
        const url = await getOrderFileUrl(orderId, row.id);
        await downloadFile(
          url,
          row['fileName'],
          btnDownloading,
          setBtnDownloading,
          setVariant,
          setCompleted,
          row,
          'lastReviewedAt',
          setRow
        );
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    };

    const onOpenButtonClick = async () => {
      try {
        const url = await getOrderFileUrl(orderId, row.id);
        await viewPdfInNewTab(url, setIsViewingFile, row['fileName']);
        
        row['lastReviewedAt'] = new Date();
        setRow(row);
      } catch (error) {
        console.error('Error viewing file:', error);
        setIsViewingFile(false);
      }
    };

    return (
      <div className={classes.viewSection}>
        <Button
          data-testid="caseRecordViewButton"
          variant="contained"
          size="small"
          className={classes.viewBtn}
          onClick={onOpenButtonClick}
          disabled={!isPdfFile}
        >
          {
            isViewingFile 
              ? (<CircularProgress
                  color="inherit"
                  size={24}
                  thickness={8}
                />) 
              : 'View'
          }
        </Button>

        <Button
          data-testid="caseDocumentDownloadButton"
          variant="contained"
          size="small"
          className={classes.viewBtn}
          disabled={!row['fileName'] || disableDownloadButton}
          onClick={onDownloadButtonClick}
        >
          {btnDownloading ? (
            <CircularProgress
              color="inherit"
              size={24}
              thickness={8}
              variant={variant}
              value={completed}
            />
          ) : (
            'Download'
          )}
        </Button>
      </div>
    );
  }

  ViewButton.propTypes = {
    classes: PropTypes.object.isRequired,
    row: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      fileName: PropTypes.string,
      lastReviewedAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
        PropTypes.instanceOf(moment),
      ])
    }), // Not required here because it's provided by EnhancedTable at runtime
    setRow: PropTypes.func, // Not required here because it's provided by EnhancedTable at runtime
    isDownloadAllowed: PropTypes.bool
  };

  ViewButton.defaultProps = {
    isDownloadAllowed: true
  };

  const customCells = {
    records: <ViewButton classes={classes}  isDownloadAllowed={isDownloadAllowed}/>,
  };

  return (
    <div>
      <Paper elevation={0} className={classes.tableTitle}>
        Other Case Documents
      </Paper>
      <EnhancedTable
        rows={expertCaseDocumentsList}
        headCells={headCells}
        customCells={customCells}
        rowsPerPageOptions={[]}
        showPagination
        pageCount={5}
        isLoading={isLoading}
        rowKey="id"
      />
    </div>
  );
};

CaseDocuments.propTypes = {
  classes: PropTypes.object.isRequired,
  expertCaseDocumentsList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      fileName: PropTypes.string,
      description: PropTypes.string,
      fileType: PropTypes.string,
      sharedBy: PropTypes.string,
      expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      lastReviewedAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
        PropTypes.instanceOf(moment),
      ])
    })
  ),
  orderId: PropTypes.string.isRequired, // GUID from C#
  isLoading: PropTypes.bool,
  isDownloadAllowed: PropTypes.bool
};

CaseDocuments.defaultProps = {
  expertCaseDocumentsList: [],
  isLoading: false,
  isDownloadAllowed: true
};

export default withStyles(tableStyles)(CaseDocuments);
